import { FieldHistoryPanel } from '../editors/field-history-panel';
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Group,
    Loader,
    NumberInput,
    Select,
    Stack,
    Switch,
    Tabs,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CityRO } from '@routellers/types/dist/city/city.ro';
import {
    CountriesEnum,
    getRecommendedLanguages,
} from '@routellers/types/dist/shared/enum/countries.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { IconArrowLeft, IconExternalLink, IconPlus, IconTrash } from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { CityContentMap } from '../components/city-content-map';
import { CityGuidePanel } from '../components/city-guide-panel';
import { EditorFooter, LanguageChips, MediaDropzone, Section } from '../components/editor';
import { contentLanguagesForCountry } from '../utils/content-languages';

type StoryRow = { title: string; body: string };
type FactRow = { label: string; value: string };
type FaqRow = { question: string; answer: string };

type LocaleDraft = {
    title: string;
    inTitle: string;
    nearTitle: string;
    heroRegion: string;
    heroPunch: string;
    heroLede: string;
    intro: string;
    metaDescription: string;
    hotelPitch: string;
    arrival: string;
    gettingAround: string;
    commonMistake: string;
    howLong: string;
    whenToGo: string;
    money: string;
    festival: string;
    story: StoryRow[];
    practical: FactRow[];
    faq: FaqRow[];
};

type FormValues = {
    title: string;
    country: CountriesEnum | null;
    photo: string;
    hubRouteImage: string;
    hubQuestImage: string;
    hubFreeImage: string;
    hubPeopleImage: string;
    hubActivityImage: string;
    readyForRoutell: boolean;
    earlyAccess: boolean;
    heroVideoAv1Url: string;
    heroVideoMp4Url: string;
    heroPosterUrl: string;
    heroFocus: string;
    heroDurationSeconds: number | '';
    locales: Partial<Record<Languages, LocaleDraft>>;
};

const SITE_ORIGIN = (process.env.REACT_APP_SITE_DOMAIN || 'https://routellers.com').replace(
    /\/$/,
    '',
);

const SITE_LOCALES = new Set(['en', 'it', 'de']);

const emptyLocale = (): LocaleDraft => ({
    title: '',
    inTitle: '',
    nearTitle: '',
    heroRegion: '',
    heroPunch: '',
    heroLede: '',
    intro: '',
    metaDescription: '',
    hotelPitch: '',
    arrival: '',
    gettingAround: '',
    commonMistake: '',
    howLong: '',
    whenToGo: '',
    money: '',
    festival: '',
    story: [],
    practical: [],
    faq: [],
});

const localeFromRow = (
    row: NonNullable<CityRO['locales']>[number] | undefined,
    fallbackTitle: string,
): LocaleDraft => ({
    title: row?.title || fallbackTitle || '',
    inTitle: row?.inTitle || '',
    nearTitle: row?.nearTitle || '',
    heroRegion: row?.heroRegion || '',
    heroPunch: row?.heroPunch || '',
    heroLede: row?.heroLede || '',
    intro: row?.intro || '',
    metaDescription: row?.metaDescription || '',
    hotelPitch: row?.hotelPitch || '',
    arrival: row?.arrival || '',
    gettingAround: row?.gettingAround || '',
    commonMistake: row?.commonMistake || '',
    howLong: row?.howLong || '',
    whenToGo: row?.whenToGo || '',
    money: row?.money || '',
    festival: row?.festival || '',
    story: (row?.story || []).map((item) => ({ title: item.title || '', body: item.body || '' })),
    practical: (row?.practical || []).map((item) => ({
        label: item.label || '',
        value: item.value || '',
    })),
    faq: (row?.faq || []).map((item) => ({
        question: item.question || '',
        answer: item.answer || '',
    })),
});

const localeFilled = (draft?: LocaleDraft) => {
    if (!draft) return false;

    return !!(
        draft.title.trim() ||
        draft.heroRegion.trim() ||
        draft.intro.trim() ||
        draft.story.length ||
        draft.practical.length ||
        draft.arrival.trim() ||
        draft.gettingAround.trim() ||
        draft.commonMistake.trim() ||
        draft.howLong.trim() ||
        draft.whenToGo.trim() ||
        draft.money.trim() ||
        draft.festival.trim() ||
        draft.faq.length
    );
};

const SCALAR_LOCALE_FIELDS = [
    'title',
    'inTitle',
    'nearTitle',
    'heroRegion',
    'heroPunch',
    'heroLede',
    'intro',
    'metaDescription',
    'hotelPitch',
    'arrival',
    'gettingAround',
    'commonMistake',
    'howLong',
    'whenToGo',
    'money',
    'festival',
] as const satisfies ReadonlyArray<keyof LocaleDraft>;

const storyCount = (draft: LocaleDraft) =>
    draft.story.filter((row) => row.title.trim() || row.body.trim()).length;

const practicalCount = (draft: LocaleDraft) =>
    draft.practical.filter((row) => row.label.trim() || row.value.trim()).length;

const faqCount = (draft: LocaleDraft) =>
    draft.faq.filter((row) => row.question.trim() || row.answer.trim()).length;

/**
 * Grey (`empty`) when this locale lags peers: fewer chapters / facts / FAQ,
 * or a scalar field that at least one other locale already has.
 */
const localeChipMark = (
    locales: Partial<Record<Languages, LocaleDraft>>,
    languages: Languages[],
    language: Languages,
): 'filled' | 'empty' => {
    const drafts = languages.map((lang) => locales[lang] || emptyLocale());
    const draft = locales[language] || emptyLocale();

    const maxStory = Math.max(0, ...drafts.map(storyCount));
    const maxPractical = Math.max(0, ...drafts.map(practicalCount));
    const maxFaq = Math.max(0, ...drafts.map(faqCount));

    const behindOnLists =
        storyCount(draft) < maxStory ||
        practicalCount(draft) < maxPractical ||
        faqCount(draft) < maxFaq;

    const missingScalar = SCALAR_LOCALE_FIELDS.some((field) => {
        const someoneHas = drafts.some((peer) => String(peer[field] || '').trim());
        const selfHas = String(draft[field] || '').trim();

        return someoneHas && !selfHas;
    });

    if (behindOnLists || missingScalar) return 'empty';

    return localeFilled(draft) ? 'filled' : 'empty';
};

const ListItemCard = ({
    title,
    onRemove,
    removeLabel,
    children,
}: {
    title: string;
    onRemove: () => void;
    removeLabel: string;
    children: ReactNode;
}) => (
    <Stack
        gap='sm'
        p='md'
        style={{
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-md)',
            background: 'var(--mantine-color-gray-0)',
        }}
    >
        <Group justify='space-between' wrap='nowrap'>
            <Text size='xs' fw={700} tt='uppercase' c='dimmed' style={{ letterSpacing: '0.04em' }}>
                {title}
            </Text>
            <ActionIcon variant='subtle' color='red' onClick={onRemove} aria-label={removeLabel}>
                <IconTrash size={16} />
            </ActionIcon>
        </Group>
        {children}
    </Stack>
);

export const CityDetailPage = () => {
    const { t } = useTranslation();
    const { cityId: cityIdParam } = useParams();
    const cityId = Number(cityIdParam);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [city, setCity] = useState<CityRO | null>(null);
    const [tab, setTab] = useState('overview');
    const [lang, setLang] = useState<Languages>(Languages.EN);

    const countryOptions = useMemo(
        () =>
            Object.values(CountriesEnum).map((value) => ({
                value,
                label: t(`enums.countries.${value}`),
            })),
        [t],
    );

    const form = useForm<FormValues>({
        initialValues: {
            title: '',
            country: null,
            photo: '',
            hubRouteImage: '',
            hubQuestImage: '',
            hubFreeImage: '',
            hubPeopleImage: '',
            hubActivityImage: '',
            readyForRoutell: false,
            earlyAccess: true,
            heroVideoAv1Url: '',
            heroVideoMp4Url: '',
            heroPosterUrl: '',
            heroFocus: '',
            heroDurationSeconds: '',
            locales: {},
        },
    });

    const contentLanguages = useMemo(
        () => contentLanguagesForCountry(form.values.country),
        [form.values.country],
    );

    useEffect(() => {
        if (!contentLanguages.includes(lang)) {
            setLang(contentLanguages[0] || Languages.EN);
        }
    }, [contentLanguages, lang]);

    useEffect(() => {
        if (!Number.isFinite(cityId)) {
            navigate('/cities', { replace: true });

            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            form.reset();
            setCity(null);
            setTab('overview');

            try {
                const { data } = await api.cities.getOne(cityId);

                if (cancelled || !data) return;

                const locales: FormValues['locales'] = {};
                for (const language of getRecommendedLanguages(data.country)) {
                    const row = data.locales?.find((item) => item.language === language);
                    locales[language] = localeFromRow(
                        row,
                        language === Languages.EN ? data.title || '' : '',
                    );
                }

                setCity(data);
                form.setValues({
                    title: data.title || '',
                    country: data.country || null,
                    photo: data.photo || '',
                    hubRouteImage: data.hubRouteImage || '',
                    hubQuestImage: data.hubQuestImage || '',
                    hubFreeImage: data.hubFreeImage || '',
                    hubPeopleImage: data.hubPeopleImage || '',
                    hubActivityImage: data.hubActivityImage || '',
                    readyForRoutell: !!data.readyForRoutell,
                    earlyAccess: data.earlyAccess !== false,
                    heroVideoAv1Url: data.heroVideoAv1Url || '',
                    heroVideoMp4Url: data.heroVideoMp4Url || '',
                    heroPosterUrl: data.heroPosterUrl || '',
                    heroFocus: data.heroFocus || '',
                    heroDurationSeconds: data.heroDurationSeconds ?? '',
                    locales,
                });
                form.resetDirty();
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                navigate('/cities', { replace: true });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityId]);

    const activeLocale = form.values.locales[lang] || emptyLocale();

    const patchLocale = <K extends keyof LocaleDraft>(field: K, value: LocaleDraft[K]) => {
        form.setFieldValue('locales', {
            ...form.values.locales,
            [lang]: { ...activeLocale, [field]: value },
        });
    };

    const chips = contentLanguages.map((language) => ({
        value: language,
        label: language.toUpperCase(),
        mark: localeChipMark(form.values.locales, contentLanguages, language),
    }));

    const showLangChips = tab !== 'overview' && tab !== 'guide' && tab !== 'routes';

    const save = async () => {
        if (!form.values.title.trim() || !form.values.country) {
            notifications.show({
                color: 'red',
                message: t('pages.cityDetail.titleCountryRequired'),
            });

            return;
        }

        setSaving(true);

        try {
            const locales = contentLanguages
                .map((language) => {
                    const draft = form.values.locales[language] || emptyLocale();
                    const title =
                        draft.title.trim() ||
                        (language === Languages.EN ? form.values.title.trim() : '');

                    if (!title && !localeFilled(draft)) return null;

                    return {
                        language,
                        title: title || form.values.title.trim(),
                        inTitle: draft.inTitle.trim() || null,
                        nearTitle: draft.nearTitle.trim() || null,
                        heroRegion: draft.heroRegion.trim() || null,
                        heroPunch: draft.heroPunch.trim() || null,
                        heroLede: draft.heroLede.trim() || null,
                        intro: draft.intro.trim() || null,
                        metaDescription: draft.metaDescription.trim() || null,
                        hotelPitch: draft.hotelPitch.trim() || null,
                        arrival: draft.arrival.trim() || null,
                        gettingAround: draft.gettingAround.trim() || null,
                        commonMistake: draft.commonMistake.trim() || null,
                        howLong: draft.howLong.trim() || null,
                        whenToGo: draft.whenToGo.trim() || null,
                        money: draft.money.trim() || null,
                        festival: draft.festival.trim() || null,
                        story: draft.story
                            .map((row) => ({
                                title: row.title.trim(),
                                body: row.body.trim(),
                            }))
                            .filter((row) => row.title || row.body),
                        practical: draft.practical
                            .map((row) => ({
                                label: row.label.trim(),
                                value: row.value.trim(),
                            }))
                            .filter((row) => row.label || row.value),
                        faq: draft.faq
                            .map((row) => ({
                                question: row.question.trim(),
                                answer: row.answer.trim(),
                            }))
                            .filter((row) => row.question || row.answer),
                    };
                })
                .filter((row): row is NonNullable<typeof row> => !!row);

            const { data } = await api.cities.update({
                id: cityId,
                data: {
                    title: form.values.title.trim(),
                    country: form.values.country,
                    photo: form.values.photo.trim() || undefined,
                    hubRouteImage: form.values.hubRouteImage.trim() || null,
                    hubQuestImage: form.values.hubQuestImage.trim() || null,
                    hubFreeImage: form.values.hubFreeImage.trim() || null,
                    hubPeopleImage: form.values.hubPeopleImage.trim() || null,
                    hubActivityImage: form.values.hubActivityImage.trim() || null,
                    readyForRoutell: form.values.readyForRoutell,
                    earlyAccess: form.values.earlyAccess,
                    heroVideoAv1Url: form.values.heroVideoAv1Url.trim() || null,
                    heroVideoMp4Url: form.values.heroVideoMp4Url.trim() || null,
                    heroPosterUrl: form.values.heroPosterUrl.trim() || null,
                    heroFocus: form.values.heroFocus.trim() || null,
                    heroDurationSeconds:
                        form.values.heroDurationSeconds === ''
                            ? null
                            : Number(form.values.heroDurationSeconds),
                    locales,
                },
            });

            if (data) {
                setCity(data);
                const nextLocales: FormValues['locales'] = {};
                for (const language of getRecommendedLanguages(data.country)) {
                    const row = data.locales?.find((item) => item.language === language);
                    nextLocales[language] = localeFromRow(
                        row,
                        language === Languages.EN ? data.title || '' : '',
                    );
                }
                form.setValues({
                    title: data.title || '',
                    country: data.country || null,
                    photo: data.photo || '',
                    hubRouteImage: data.hubRouteImage || '',
                    hubQuestImage: data.hubQuestImage || '',
                    hubFreeImage: data.hubFreeImage || '',
                    hubPeopleImage: data.hubPeopleImage || '',
                    hubActivityImage: data.hubActivityImage || '',
                    readyForRoutell: !!data.readyForRoutell,
                    earlyAccess: data.earlyAccess !== false,
                    heroVideoAv1Url: data.heroVideoAv1Url || '',
                    heroVideoMp4Url: data.heroVideoMp4Url || '',
                    heroPosterUrl: data.heroPosterUrl || '',
                    heroFocus: data.heroFocus || '',
                    heroDurationSeconds: data.heroDurationSeconds ?? '',
                    locales: nextLocales,
                });
                form.resetDirty();
            }

            notifications.show({ color: 'green', message: t('pages.cityDetail.saved') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const ok = window.confirm(
            t('pages.cityDetail.deleteConfirm', { id: cityId, title: city?.title }),
        );

        if (!ok) return;

        setDeleting(true);

        try {
            await api.cities.remove(cityId);
            notifications.show({ color: 'green', message: t('pages.cityDetail.deleted') });
            navigate('/cities');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setDeleting(false);
        }
    };

    const coords =
        city?.coordinates?.length === 2
            ? `${city.coordinates[1]?.toFixed(4)}, ${city.coordinates[0]?.toFixed(4)}`
            : null;

    const siteLocale = SITE_LOCALES.has(lang) ? lang : 'en';

    if (loading || !city) {
        return (
            <Stack gap='md'>
                <Button
                    component={Link}
                    to='/cities'
                    variant='subtle'
                    leftSection={<IconArrowLeft size={16} />}
                    w='fit-content'
                >
                    {t('pages.cityDetail.back')}
                </Button>
                <Loader />
            </Stack>
        );
    }

    return (
        <Stack
            gap={0}
            style={{
                flex: 1,
                minHeight: 0,
                height: 'calc(100dvh - var(--app-shell-header-offset, 60px) - 2 * var(--mantine-spacing-lg))',
                maxHeight:
                    'calc(100dvh - var(--app-shell-header-offset, 60px) - 2 * var(--mantine-spacing-lg))',
            }}
        >
            <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }} pb='md'>
                <Stack gap='md'>
                    <Group justify='space-between' align='flex-start' wrap='wrap'>
                        <Stack gap={4}>
                            <Button
                                component={Link}
                                to='/cities'
                                variant='subtle'
                                leftSection={<IconArrowLeft size={16} />}
                                w='fit-content'
                                px={0}
                            >
                                {t('pages.cityDetail.back')}
                            </Button>
                            <Group gap='sm'>
                                <Text fw={700} size='xl'>
                                    {city.title}
                                </Text>
                                <Badge
                                    color={city.readyForRoutell ? 'green' : 'gray'}
                                    variant='light'
                                >
                                    {city.readyForRoutell
                                        ? t('pages.cityDetail.readyBadge')
                                        : t('pages.cities.hidden')}
                                </Badge>
                                {city.earlyAccess !== false ? (
                                    <Badge color='yellow' variant='light'>
                                        {t('pages.cityDetail.earlyAccessBadge')}
                                    </Badge>
                                ) : null}
                                {form.isDirty() && (
                                    <Badge color='orange' variant='dot'>
                                        {t('pages.cityDetail.unsaved')}
                                    </Badge>
                                )}
                            </Group>
                            <Group gap={6} align='center' wrap='nowrap'>
                                <Text size='sm' c='dimmed'>
                                    {[city.slug, city.country?.toString().toUpperCase(), coords]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Text>
                                {city.slug && (
                                    <ActionIcon
                                        component='a'
                                        href={`${SITE_ORIGIN}/${siteLocale}/${city.slug}/`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        variant='subtle'
                                        color='gray'
                                        size='sm'
                                        aria-label={t('pages.cityDetail.openOnSite')}
                                        title={t('pages.cityDetail.openOnSite')}
                                    >
                                        <IconExternalLink size={14} />
                                    </ActionIcon>
                                )}
                            </Group>
                        </Stack>
                        <Button
                            component={Link}
                            to={`/activities?cityId=${city.id}`}
                            variant='light'
                        >
                            {t('pages.activities.title')}
                        </Button>
                    </Group>

                    <Tabs value={tab} onChange={(value) => value && setTab(value)}>
                        <Tabs.List mb={showLangChips ? 'sm' : 'md'}>
                            <Tabs.Tab value='field'>В поле</Tabs.Tab>
                            <Tabs.Tab value='overview'>
                                {t('pages.cityDetail.tabs.overview')}
                            </Tabs.Tab>
                            <Tabs.Tab value='routes'>{t('pages.cityDetail.tabs.routes')}</Tabs.Tab>
                            <Tabs.Tab value='guide'>{t('pages.cityDetail.tabs.guide')}</Tabs.Tab>
                            <Tabs.Tab value='locales'>
                                {t('pages.cityDetail.tabs.locales')}
                            </Tabs.Tab>
                            <Tabs.Tab value='hero'>{t('pages.cityDetail.tabs.hero')}</Tabs.Tab>
                            <Tabs.Tab value='story'>{t('pages.cityDetail.tabs.story')}</Tabs.Tab>
                            <Tabs.Tab value='practical'>
                                {t('pages.cityDetail.tabs.practical')}
                            </Tabs.Tab>
                            <Tabs.Tab value='faq'>{t('pages.cityDetail.tabs.faq')}</Tabs.Tab>
                            <Tabs.Tab value='seo'>{t('pages.cityDetail.tabs.seo')}</Tabs.Tab>
                        </Tabs.List>

                        {showLangChips && (
                            <Stack gap={0} mb='md'>
                                <LanguageChips
                                    languages={chips}
                                    value={lang}
                                    onChange={(value) => setLang(value as Languages)}
                                />
                            </Stack>
                        )}

                        <Tabs.Panel value='field' pt='md' keepMounted={false}>
                            <FieldHistoryPanel cityId={cityId} />
                        </Tabs.Panel>
                        <Tabs.Panel value='overview' pt='md'>
                            <Stack gap='md' maw={720}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.identity.title')}
                                    description={t('pages.cityDetail.identity.description')}
                                >
                                    <TextInput
                                        label={t('pages.cityDetail.identity.titleLabel')}
                                        required
                                        {...form.getInputProps('title')}
                                    />
                                    <Select
                                        label={t('pages.cityDetail.identity.country')}
                                        data={countryOptions}
                                        searchable
                                        required
                                        {...form.getInputProps('country')}
                                    />
                                    <Switch
                                        label={t('pages.cityDetail.identity.readyToLaunch')}
                                        checked={form.values.readyForRoutell}
                                        onChange={(e) =>
                                            form.setFieldValue(
                                                'readyForRoutell',
                                                e.currentTarget.checked,
                                            )
                                        }
                                    />
                                    <Switch
                                        label={t('pages.cityDetail.identity.earlyAccess')}
                                        description={t('pages.cityDetail.identity.earlyAccessHint')}
                                        checked={form.values.earlyAccess}
                                        onChange={(e) =>
                                            form.setFieldValue(
                                                'earlyAccess',
                                                e.currentTarget.checked,
                                            )
                                        }
                                    />
                                    {coords && (
                                        <Text size='sm' c='dimmed'>
                                            {t('pages.cityDetail.identity.coordinates', { coords })}
                                        </Text>
                                    )}
                                </Section>

                                <Section
                                    boxed
                                    title={t('pages.cityDetail.catalogPhoto.title')}
                                    description={t('pages.cityDetail.catalogPhoto.description')}
                                >
                                    <MediaDropzone
                                        kind='image'
                                        imageType={ImageTypeEnum.POSTER}
                                        allowUrl={false}
                                        value={form.values.photo || null}
                                        onChange={(url) => form.setFieldValue('photo', url || '')}
                                    />
                                </Section>

                                <Section
                                    boxed
                                    title={t('pages.cityDetail.hubImages.title')}
                                    description={t('pages.cityDetail.hubImages.description')}
                                >
                                    {(
                                        [
                                            ['hubRouteImage', 'route'],
                                            ['hubQuestImage', 'quest'],
                                            ['hubFreeImage', 'free'],
                                            ['hubPeopleImage', 'people'],
                                            ['hubActivityImage', 'activity'],
                                        ] as const
                                    ).map(([field, key]) => (
                                        <MediaDropzone
                                            key={field}
                                            label={t(`pages.cityDetail.hubImages.${key}`)}
                                            kind='image'
                                            imageType={ImageTypeEnum.POSTER}
                                            allowUrl={false}
                                            value={form.values[field] || null}
                                            onChange={(url) => form.setFieldValue(field, url || '')}
                                        />
                                    ))}
                                </Section>

                                <Section
                                    boxed
                                    title={t('pages.cityDetail.heroVideo.title')}
                                    description={t('pages.cityDetail.heroVideo.description')}
                                >
                                    <MediaDropzone
                                        label={t('pages.cityDetail.heroVideo.av1')}
                                        kind='video'
                                        allowUrl={false}
                                        value={form.values.heroVideoAv1Url || null}
                                        onChange={(url) =>
                                            form.setFieldValue('heroVideoAv1Url', url || '')
                                        }
                                    />
                                    <MediaDropzone
                                        label={t('pages.cityDetail.heroVideo.h264')}
                                        kind='video'
                                        allowUrl={false}
                                        value={form.values.heroVideoMp4Url || null}
                                        onChange={(url) =>
                                            form.setFieldValue('heroVideoMp4Url', url || '')
                                        }
                                    />
                                    <NumberInput
                                        label={t('pages.cityDetail.heroVideo.duration')}
                                        min={0}
                                        maw={200}
                                        value={form.values.heroDurationSeconds}
                                        onChange={(value) =>
                                            form.setFieldValue(
                                                'heroDurationSeconds',
                                                value === '' || value == null ? '' : Number(value),
                                            )
                                        }
                                    />
                                </Section>

                                <Section
                                    boxed
                                    title={t('pages.cityDetail.heroStill.title')}
                                    description={t('pages.cityDetail.heroStill.description')}
                                >
                                    <MediaDropzone
                                        label={t('pages.cityDetail.heroStill.poster')}
                                        kind='image'
                                        imageType={ImageTypeEnum.POSTER}
                                        allowUrl={false}
                                        value={form.values.heroPosterUrl || null}
                                        onChange={(url) =>
                                            form.setFieldValue('heroPosterUrl', url || '')
                                        }
                                    />
                                    <TextInput
                                        label={t('pages.cityDetail.heroStill.focus')}
                                        placeholder={t(
                                            'pages.cityDetail.heroStill.focusPlaceholder',
                                        )}
                                        {...form.getInputProps('heroFocus')}
                                    />
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='routes' pt='md' keepMounted={false}>
                            <CityContentMap cityId={city.id} />
                        </Tabs.Panel>

                        <Tabs.Panel value='guide' pt='md'>
                            <CityGuidePanel cityId={city.id} country={city.country} />
                        </Tabs.Panel>

                        <Tabs.Panel value='locales' pt='md'>
                            <Stack gap='md' maw={720}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.displayNames.title')}
                                    description={t('pages.cityDetail.displayNames.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <TextInput
                                        label={t('pages.cityDetail.displayNames.titleLabel')}
                                        value={activeLocale.title}
                                        onChange={(e) =>
                                            patchLocale('title', e.currentTarget.value)
                                        }
                                    />
                                    <TextInput
                                        label={t('pages.cityDetail.displayNames.inTitle')}
                                        description={t('pages.cityDetail.displayNames.inTitleHint')}
                                        value={activeLocale.inTitle}
                                        onChange={(e) =>
                                            patchLocale('inTitle', e.currentTarget.value)
                                        }
                                    />
                                    <TextInput
                                        label={t('pages.cityDetail.displayNames.nearTitle')}
                                        description={t(
                                            'pages.cityDetail.displayNames.nearTitleHint',
                                        )}
                                        value={activeLocale.nearTitle}
                                        onChange={(e) =>
                                            patchLocale('nearTitle', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='hero' pt='md'>
                            <Stack gap='md' maw={720}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.heroHeadline.title')}
                                    description={t('pages.cityDetail.heroHeadline.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <TextInput
                                        label={t('pages.cityDetail.heroHeadline.region')}
                                        description={t('pages.cityDetail.heroHeadline.regionHint')}
                                        value={activeLocale.heroRegion}
                                        onChange={(e) =>
                                            patchLocale('heroRegion', e.currentTarget.value)
                                        }
                                    />
                                    <TextInput
                                        label={t('pages.cityDetail.heroHeadline.punch')}
                                        description={t('pages.cityDetail.heroHeadline.punchHint')}
                                        value={activeLocale.heroPunch}
                                        onChange={(e) =>
                                            patchLocale('heroPunch', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.heroLede.title')}
                                    description={t('pages.cityDetail.heroLede.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Textarea
                                        label={t('pages.cityDetail.heroLede.lede')}
                                        minRows={3}
                                        value={activeLocale.heroLede}
                                        onChange={(e) =>
                                            patchLocale('heroLede', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='story' pt='md'>
                            <Stack gap='md' maw={800}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.storyIntro.title')}
                                    description={t('pages.cityDetail.storyIntro.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Textarea
                                        label={t('pages.cityDetail.storyIntro.opening')}
                                        minRows={4}
                                        value={activeLocale.intro}
                                        onChange={(e) =>
                                            patchLocale('intro', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.storyChapters.title')}
                                    description={t('pages.cityDetail.storyChapters.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Stack gap='sm'>
                                        {activeLocale.story.map((row, index) => (
                                            <ListItemCard
                                                key={`story-${index}-${row.title.slice(0, 12)}`}
                                                title={t('pages.cityDetail.storyChapters.chapter', {
                                                    n: index + 1,
                                                })}
                                                removeLabel={t('common.remove')}
                                                onRemove={() =>
                                                    patchLocale(
                                                        'story',
                                                        activeLocale.story.filter(
                                                            (_, i) => i !== index,
                                                        ),
                                                    )
                                                }
                                            >
                                                <TextInput
                                                    label={t(
                                                        'pages.cityDetail.storyChapters.chapterTitle',
                                                    )}
                                                    value={row.title}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.story];
                                                        next[index] = {
                                                            ...row,
                                                            title: e.currentTarget.value,
                                                        };
                                                        patchLocale('story', next);
                                                    }}
                                                />
                                                <Textarea
                                                    label={t(
                                                        'pages.cityDetail.storyChapters.chapterBody',
                                                    )}
                                                    minRows={3}
                                                    value={row.body}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.story];
                                                        next[index] = {
                                                            ...row,
                                                            body: e.currentTarget.value,
                                                        };
                                                        patchLocale('story', next);
                                                    }}
                                                />
                                            </ListItemCard>
                                        ))}
                                        <Button
                                            variant='light'
                                            leftSection={<IconPlus size={16} />}
                                            onClick={() =>
                                                patchLocale('story', [
                                                    ...activeLocale.story,
                                                    { title: '', body: '' },
                                                ])
                                            }
                                        >
                                            {t('pages.cityDetail.storyChapters.addChapter')}
                                        </Button>
                                    </Stack>
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='practical' pt='md'>
                            <Stack gap='md' maw={800}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.practicalFacts.title')}
                                    description={t('pages.cityDetail.practicalFacts.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Stack gap='sm'>
                                        {activeLocale.practical.map((row, index) => (
                                            <ListItemCard
                                                key={`fact-${index}-${row.label.slice(0, 12)}`}
                                                title={t('pages.cityDetail.practicalFacts.fact', {
                                                    n: index + 1,
                                                })}
                                                removeLabel={t('common.remove')}
                                                onRemove={() =>
                                                    patchLocale(
                                                        'practical',
                                                        activeLocale.practical.filter(
                                                            (_, i) => i !== index,
                                                        ),
                                                    )
                                                }
                                            >
                                                <TextInput
                                                    label={t(
                                                        'pages.cityDetail.practicalFacts.label',
                                                    )}
                                                    value={row.label}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.practical];
                                                        next[index] = {
                                                            ...row,
                                                            label: e.currentTarget.value,
                                                        };
                                                        patchLocale('practical', next);
                                                    }}
                                                />
                                                <Textarea
                                                    label={t(
                                                        'pages.cityDetail.practicalFacts.value',
                                                    )}
                                                    minRows={2}
                                                    value={row.value}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.practical];
                                                        next[index] = {
                                                            ...row,
                                                            value: e.currentTarget.value,
                                                        };
                                                        patchLocale('practical', next);
                                                    }}
                                                />
                                            </ListItemCard>
                                        ))}
                                        <Button
                                            variant='light'
                                            leftSection={<IconPlus size={16} />}
                                            onClick={() =>
                                                patchLocale('practical', [
                                                    ...activeLocale.practical,
                                                    { label: '', value: '' },
                                                ])
                                            }
                                        >
                                            {t('pages.cityDetail.practicalFacts.addFact')}
                                        </Button>
                                    </Stack>
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='faq' pt='md'>
                            <Stack gap='md' maw={800}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.faq.title')}
                                    description={t('pages.cityDetail.faq.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Stack gap='sm'>
                                        {activeLocale.faq.map((row, index) => (
                                            <ListItemCard
                                                key={`faq-${index}-${row.question.slice(0, 12)}`}
                                                title={t('pages.cityDetail.faq.question', {
                                                    n: index + 1,
                                                })}
                                                removeLabel={t('common.remove')}
                                                onRemove={() =>
                                                    patchLocale(
                                                        'faq',
                                                        activeLocale.faq.filter(
                                                            (_, i) => i !== index,
                                                        ),
                                                    )
                                                }
                                            >
                                                <TextInput
                                                    label={t('pages.cityDetail.faq.questionLabel')}
                                                    value={row.question}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.faq];
                                                        next[index] = {
                                                            ...row,
                                                            question: e.currentTarget.value,
                                                        };
                                                        patchLocale('faq', next);
                                                    }}
                                                />
                                                <Textarea
                                                    label={t('pages.cityDetail.faq.answer')}
                                                    minRows={3}
                                                    value={row.answer}
                                                    onChange={(e) => {
                                                        const next = [...activeLocale.faq];
                                                        next[index] = {
                                                            ...row,
                                                            answer: e.currentTarget.value,
                                                        };
                                                        patchLocale('faq', next);
                                                    }}
                                                />
                                            </ListItemCard>
                                        ))}
                                        <Button
                                            variant='light'
                                            leftSection={<IconPlus size={16} />}
                                            onClick={() =>
                                                patchLocale('faq', [
                                                    ...activeLocale.faq,
                                                    { question: '', answer: '' },
                                                ])
                                            }
                                        >
                                            {t('pages.cityDetail.faq.addQuestion')}
                                        </Button>
                                    </Stack>
                                </Section>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value='seo' pt='md'>
                            <Stack gap='md' maw={720}>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.seoSearch.title')}
                                    description={t('pages.cityDetail.seoSearch.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Textarea
                                        label={t('pages.cityDetail.seoSearch.metaDescription')}
                                        minRows={3}
                                        value={activeLocale.metaDescription}
                                        onChange={(e) =>
                                            patchLocale('metaDescription', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                                <Section
                                    boxed
                                    title={t('pages.cityDetail.partnerPitch.title')}
                                    description={t('pages.cityDetail.partnerPitch.description', {
                                        lang: lang.toUpperCase(),
                                    })}
                                >
                                    <Textarea
                                        label={t('pages.cityDetail.partnerPitch.hotelPitch')}
                                        minRows={3}
                                        value={activeLocale.hotelPitch}
                                        onChange={(e) =>
                                            patchLocale('hotelPitch', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.arrival')}
                                        description={t('pages.cityDetail.arrival.arrivalHint')}
                                        minRows={3}
                                        value={activeLocale.arrival}
                                        onChange={(e) =>
                                            patchLocale('arrival', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.gettingAround')}
                                        description={t(
                                            'pages.cityDetail.arrival.gettingAroundHint',
                                        )}
                                        minRows={3}
                                        value={activeLocale.gettingAround}
                                        onChange={(e) =>
                                            patchLocale('gettingAround', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.commonMistake')}
                                        description={t(
                                            'pages.cityDetail.arrival.commonMistakeHint',
                                        )}
                                        minRows={3}
                                        value={activeLocale.commonMistake}
                                        onChange={(e) =>
                                            patchLocale('commonMistake', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.howLong')}
                                        description={t('pages.cityDetail.arrival.howLongHint')}
                                        minRows={2}
                                        value={activeLocale.howLong}
                                        onChange={(e) =>
                                            patchLocale('howLong', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.whenToGo')}
                                        description={t('pages.cityDetail.arrival.whenToGoHint')}
                                        minRows={3}
                                        value={activeLocale.whenToGo}
                                        onChange={(e) =>
                                            patchLocale('whenToGo', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.money')}
                                        description={t('pages.cityDetail.arrival.moneyHint')}
                                        minRows={2}
                                        value={activeLocale.money}
                                        onChange={(e) =>
                                            patchLocale('money', e.currentTarget.value)
                                        }
                                    />
                                    <Textarea
                                        label={t('pages.cityDetail.arrival.festival')}
                                        description={t('pages.cityDetail.arrival.festivalHint')}
                                        minRows={2}
                                        value={activeLocale.festival}
                                        onChange={(e) =>
                                            patchLocale('festival', e.currentTarget.value)
                                        }
                                    />
                                </Section>
                            </Stack>
                        </Tabs.Panel>
                    </Tabs>
                </Stack>
            </Box>

            <Box
                maw={720}
                w='100%'
                pt='sm'
                style={{
                    flexShrink: 0,
                    borderTop: '1px solid var(--mantine-color-gray-2)',
                    background: 'var(--mantine-color-gray-0)',
                }}
            >
                <EditorFooter
                    onCancel={() => navigate('/cities')}
                    onSave={save}
                    saving={saving}
                    saveLabel={t('pages.cityDetail.saveCity')}
                    left={
                        <Button color='red' variant='light' loading={deleting} onClick={remove}>
                            {t('common.delete')}
                        </Button>
                    }
                />
            </Box>
        </Stack>
    );
};
